import { useEffect, useMemo, useRef, useState } from 'react';

import { useDosespotPatientInfoQuery } from '@/entities/rx';
import { useMyEmployeeRecordQuery } from '@/entities/me';
import { useMyIdentificationQuery } from '@/entities/identification';
import { useUserInfoQuery } from '@/entities/user-info';

import {
  createDocumentDraft,
  type DocumentDraft,
  type DocumentDraftModifier,
  type DocumentDraftSeed,
  type DocumentDraftStore,
  useDocumentDraftsQuery,
} from '@/entities/document-draft';
import type { DocumentEditorLoadState } from './documentEditorTypes';

interface UseDocumentDraftLoaderProps {
  patientId: string;
  store: DocumentDraftStore;
  draftId?: string;
  seed?: DocumentDraftSeed;
}

export interface UseDocumentDraftLoaderResult {
  loadedDraft: DocumentDraft | null;
  loadKey: string;
  loadState: DocumentEditorLoadState;
  isNewDraft: boolean;
  currentModifier: DocumentDraftModifier | null;
  hasLoadError: boolean;
  requiredDataLoading: boolean;
}

function formatPrescriberName(displayName: string | null | undefined): string {
  const name = displayName?.trim() ?? '';
  if (name === '') {
    return '';
  }
  return /^dr\.?\s/i.test(name) ? name : `Dr. ${name}`;
}

function formatDateInputValue(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

const DEFAULT_MODIFIER_DISPLAY_NAME = 'Admin';

export function useDocumentDraftLoader({
  patientId,
  store,
  draftId,
  seed,
}: UseDocumentDraftLoaderProps): UseDocumentDraftLoaderResult {
  const draftsQuery = useDocumentDraftsQuery(patientId, store);
  const employeeRecordQuery = useMyEmployeeRecordQuery();
  const identificationQuery = useMyIdentificationQuery();
  const dosespotPatientInfoQuery = useDosespotPatientInfoQuery(
    draftId === undefined ? patientId : undefined,
    { sentryOnError: false, toastOnError: false }
  );
  const patientInfoQuery = useUserInfoQuery(draftId === undefined ? patientId : undefined);
  const isNewDraft = draftId === undefined;
  const loadKey = `${patientId}:${draftId ?? 'new'}`;
  // The initial save refreshes the drafts query. Retain the draft created by
  // createDocumentDraft so that refresh keeps both its id and initial contents.
  const initialDraftRef = useRef<DocumentDraft | null>(null);
  const [initialDraft, setInitialDraft] = useState<DocumentDraft | null>(null);

  const currentModifier = useMemo<DocumentDraftModifier | null>(() => {
    const userId = identificationQuery.data?.userId?.trim() ?? '';
    const displayName =
      employeeRecordQuery.data?.displayName?.trim() || DEFAULT_MODIFIER_DISPLAY_NAME;
    if (userId === '') {
      return null;
    }
    return {
      userId,
      displayName,
    };
  }, [employeeRecordQuery.data?.displayName, identificationQuery.data?.userId]);

  const hasLoadError =
    draftsQuery.isError ||
    employeeRecordQuery.isError ||
    identificationQuery.isError ||
    patientInfoQuery.isError ||
    (!employeeRecordQuery.isLoading && !identificationQuery.isLoading && currentModifier === null);
  const sourceDataLoading =
    draftsQuery.isLoading ||
    employeeRecordQuery.isLoading ||
    identificationQuery.isLoading ||
    (isNewDraft && patientInfoQuery.isLoading);
  const requiredDataLoading =
    sourceDataLoading || (isNewDraft && initialDraft === null && !hasLoadError);

  // DoseSpot only enriches a new draft with an optional MRN. A missing or
  // temporarily unavailable DoseSpot record must not prevent the editor
  // from opening or saving the draft.

  useEffect(() => {
    if (
      !isNewDraft ||
      sourceDataLoading ||
      hasLoadError ||
      currentModifier === null ||
      initialDraft !== null
    ) {
      return;
    }
    if (initialDraftRef.current !== null) {
      setInitialDraft(initialDraftRef.current);
      return;
    }

    const patientInfo = patientInfoQuery.data?.patientInfo;
    const newDraftMarkdown = store.buildNewDraftMarkdown({
      prescriberName: formatPrescriberName(employeeRecordQuery.data?.displayName),
      date: formatDateInputValue(new Date()),
      patientName: patientInfo?.name?.trim() ?? '',
      patientDateOfBirth: patientInfo?.date_of_birth ?? '',
      patientMrn: dosespotPatientInfoQuery.data?.NonDoseSpotMedicalRecordNumber?.trim() ?? '',
      recipient: { name: '', organization: '', faxNumber: '' },
    });
    const createdDraft = createDocumentDraft(
      {
        bodyMarkdown: seed?.bodyMarkdown ?? newDraftMarkdown,
        attachments: seed?.attachments,
      },
      currentModifier
    );
    initialDraftRef.current = createdDraft;
    setInitialDraft(createdDraft);
  }, [
    currentModifier,
    dosespotPatientInfoQuery.data?.NonDoseSpotMedicalRecordNumber,
    employeeRecordQuery.data?.displayName,
    hasLoadError,
    initialDraft,
    isNewDraft,
    patientInfoQuery.data?.patientInfo,
    seed,
    sourceDataLoading,
    store,
  ]);

  const loadedDraft = useMemo(() => {
    if (requiredDataLoading || hasLoadError || currentModifier === null) {
      return null;
    }

    const existing = draftId
      ? (draftsQuery.data ?? []).find((candidate) => candidate.id === draftId)
      : undefined;
    if (existing !== undefined) {
      return existing;
    }
    if (!isNewDraft) {
      return null;
    }
    return initialDraft;
  }, [
    currentModifier,
    draftId,
    draftsQuery.data,
    hasLoadError,
    initialDraft,
    isNewDraft,
    requiredDataLoading,
  ]);

  const loadState: DocumentEditorLoadState = hasLoadError
    ? 'error'
    : requiredDataLoading
      ? 'loading'
      : draftId !== undefined && loadedDraft === null
        ? 'missing'
        : 'ready';

  return {
    loadedDraft,
    loadKey,
    loadState,
    isNewDraft,
    currentModifier,
    hasLoadError,
    requiredDataLoading,
  };
}
